import type { Match } from "../types/index";
import { showNotification } from "../utils/ui.js";
import { validateMatchScore, SetScore } from "../utils/padelScoringValidator.js";
import { teamDisplayName } from "../utils/matchTeams.js";
import { renderReportForm } from "./reportResult/formTemplate.js";

export class ReportResultComponent {
  private container: HTMLElement;
  private match: Match;
  private onSubmit: (data: any) => Promise<void>;
  public isDirty: boolean = false;

  constructor(container: HTMLElement, match: Match, onSubmit: (data: any) => Promise<void>) {
    this.container = container;
    this.match = match;
    this.onSubmit = onSubmit;
  }

  render(): void {
    const team1Name = teamDisplayName(this.match, 0, "Player 1");
    const team2Name = teamDisplayName(this.match, 1, "Player 2");

    // Extract existing scores if available (for edit mode)
    let sets: any[] = [];
    if (this.match.sets) {
      sets = typeof this.match.sets === "string" ? JSON.parse(this.match.sets) : this.match.sets;
    }

    const getScore = (setIndex: number, pairIndex: 'pair1score' | 'pair2score') => {
      const set = sets[setIndex];
      if (set && set[pairIndex] !== undefined && set[pairIndex] !== null) {
        return set[pairIndex];
      }
      return "";
    };

    // Create modal overlay
    this.container.innerHTML = renderReportForm({
      matchId: this.match.id,
      team1Name,
      team2Name,
      s1t1: getScore(0, 'pair1score'),
      s1t2: getScore(0, 'pair2score'),
      s2t1: getScore(1, 'pair1score'),
      s2t2: getScore(1, 'pair2score'),
      s3t1: getScore(2, 'pair1score'),
      s3t2: getScore(2, 'pair2score'),
    });

    this.setupEventListeners();
    this.setupInputListeners();
    
    // Initialize state based on populated values
    this.updateSummary();
    this.updateSet3State();
    this.validateForm();

    this.setupAutoFocus();
  }

  private setupAutoFocus(): void {
    // Auto-focus the first input
    setTimeout(() => {
      const firstInput = this.container.querySelector(
        'input[name="set-1-team1"]'
      ) as HTMLInputElement;
      if (firstInput) firstInput.focus();
    }, 100);
  }

  private setupInputListeners(): void {
    const inputs = this.container.querySelectorAll(".set-input");
    inputs.forEach((input) => {
      input.addEventListener("input", () => {
        this.isDirty = true;
        this.updateSummary();
        this.updateSet3State();
        this.validateForm();
      });
    });
  }

  private updateSet3State(): void {
    const s1t1 = (this.container.querySelector('input[name="set-1-team1"]') as HTMLInputElement);
    const s1t2 = (this.container.querySelector('input[name="set-1-team2"]') as HTMLInputElement);
    const s2t1 = (this.container.querySelector('input[name="set-2-team1"]') as HTMLInputElement);
    const s2t2 = (this.container.querySelector('input[name="set-2-team2"]') as HTMLInputElement);
    
    const s3t1 = (this.container.querySelector('input[name="set-3-team1"]') as HTMLInputElement);
    const s3t2 = (this.container.querySelector('input[name="set-3-team2"]') as HTMLInputElement);
    
    const s3Wrapper1 = this.container.querySelector('#set-3-wrapper-team1');
    const s3Wrapper2 = this.container.querySelector('#set-3-wrapper-team2');

    // Check if Set 1 and Set 2 have values entered
    const isSet1Filled = s1t1.value !== "" && s1t2.value !== "";
    const isSet2Filled = s2t1.value !== "" && s2t2.value !== "";
    
    let enableSet3 = false;

    if (isSet1Filled && isSet2Filled) {
      const s1Score1 = parseInt(s1t1.value);
      const s1Score2 = parseInt(s1t2.value);
      const s2Score1 = parseInt(s2t1.value);
      const s2Score2 = parseInt(s2t2.value);

      // Determine winners of first two sets
      const team1WinsSet1 = s1Score1 > s1Score2;
      const team2WinsSet1 = s1Score2 > s1Score1;
      const team1WinsSet2 = s2Score1 > s2Score2;
      const team2WinsSet2 = s2Score2 > s2Score1;

      // Enable Set 3 only if sets are split (1-1)
      if ((team1WinsSet1 && team2WinsSet2) || (team2WinsSet1 && team1WinsSet2)) {
        enableSet3 = true;
      }
    }

    if (enableSet3) {
      s3t1.disabled = false;
      s3t2.disabled = false;
      s3Wrapper1?.classList.remove('opacity-40');
      s3Wrapper2?.classList.remove('opacity-40');
    } else {
      s3t1.disabled = true;
      s3t2.disabled = true;
      s3Wrapper1?.classList.add('opacity-40');
      s3Wrapper2?.classList.add('opacity-40');
      
      // Clear Set 3 values if it becomes disabled
      if (s3t1.value !== "" || s3t2.value !== "") {
        s3t1.value = "";
        s3t2.value = "";
      }
    }
  }

  private validateForm(): void {
    const sets: SetScore[] = [];
    const submitBtn = this.container.querySelector("#submit-btn") as HTMLButtonElement;
    const errorDiv = this.container.querySelector("#validation-error") as HTMLElement;
    
    // Helper to get values
    const getVal = (name: string) => {
      const el = this.container.querySelector(`input[name="${name}"]`) as HTMLInputElement;
      return el && el.value !== "" ? parseInt(el.value) : null;
    };

    // Collect Set 1
    const s1t1 = getVal("set-1-team1");
    const s1t2 = getVal("set-1-team2");
    if (s1t1 !== null && s1t2 !== null) sets.push({ pair1score: s1t1, pair2score: s1t2 });

    // Collect Set 2
    const s2t1 = getVal("set-2-team1");
    const s2t2 = getVal("set-2-team2");
    if (s2t1 !== null && s2t2 !== null) sets.push({ pair1score: s2t1, pair2score: s2t2 });

    // Collect Set 3
    const s3t1 = getVal("set-3-team1");
    const s3t2 = getVal("set-3-team2");
    if (s3t1 !== null && s3t2 !== null) sets.push({ pair1score: s3t1, pair2score: s3t2 });

    // Run Validation
    const result = validateMatchScore(sets);

    // Reset styles
    this.container.querySelectorAll(".set-input").forEach(el => {
      el.classList.remove("border-red-500", "focus:border-red-500", "text-red-600");
      el.classList.add("focus:border-primary");
    });

    if (!result.isValid) {
      // Show error
      if (errorDiv) {
        errorDiv.textContent = result.error || "Invalid scores";
        errorDiv.classList.remove("hidden");
      }
      // Disable submit
      if (submitBtn) submitBtn.disabled = true;

      // Highlight invalid sets
      if (result.invalidSets) {
        result.invalidSets.forEach(setIndex => {
          const inputs = this.container.querySelectorAll(`input[name^="set-${setIndex + 1}"]`);
          inputs.forEach(el => {
            el.classList.add("border-red-500", "focus:border-red-500", "text-red-600");
            el.classList.remove("focus:border-primary");
          });
        });
      }
    } else {
      // Valid state
      if (errorDiv) errorDiv.classList.add("hidden");
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  private updateSummary(): void {
    const set1Team1 = parseInt(
      (this.container.querySelector('input[name="set-1-team1"]') as HTMLInputElement)
        ?.value || "0"
    );
    const set1Team2 = parseInt(
      (this.container.querySelector('input[name="set-1-team2"]') as HTMLInputElement)
        ?.value || "0"
    );

    const set2Team1 = parseInt(
      (this.container.querySelector('input[name="set-2-team1"]') as HTMLInputElement)
        ?.value || "0"
    );
    const set2Team2 = parseInt(
      (this.container.querySelector('input[name="set-2-team2"]') as HTMLInputElement)
        ?.value || "0"
    );

    const set3Team1 = parseInt(
      (this.container.querySelector('input[name="set-3-team1"]') as HTMLInputElement)
        ?.value || "0"
    );
    const set3Team2 = parseInt(
      (this.container.querySelector('input[name="set-3-team2"]') as HTMLInputElement)
        ?.value || "0"
    );

    // Calculate set wins
    let team1Wins = 0;
    let team2Wins = 0;

    if (set1Team1 > 0 || set1Team2 > 0) {
      if (set1Team1 > set1Team2) team1Wins++;
      else if (set1Team2 > set1Team1) team2Wins++;
    }

    if (set2Team1 > 0 || set2Team2 > 0) {
      if (set2Team1 > set2Team2) team1Wins++;
      else if (set2Team2 > set2Team1) team2Wins++;
    }

    if (set3Team1 > 0 || set3Team2 > 0) {
      if (set3Team1 > set3Team2) team1Wins++;
      else if (set3Team2 > set3Team1) team2Wins++;
    }

    const team1Total = this.container.querySelector("#team1-total");
    const team2Total = this.container.querySelector("#team2-total");

    if (team1Total) team1Total.textContent = String(team1Wins);
    if (team2Total) team2Total.textContent = String(team2Wins);
  }

  private setupEventListeners(): void {
    const form = this.container.querySelector("#result-form") as HTMLFormElement;
    const cancelBtn = this.container.querySelector("#cancel-btn") as HTMLButtonElement;
    const closeBtn = this.container.querySelector("#close-modal-btn") as HTMLButtonElement;
    const overlay = this.container.querySelector("#report-modal-overlay") as HTMLElement;

    // Cancel button
    if (cancelBtn) {
      cancelBtn.addEventListener("click", () => this.closeModal());
    }

    // Close button
    if (closeBtn) {
      closeBtn.addEventListener("click", () => this.closeModal());
    }

    // Close on overlay click
    if (overlay) {
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) this.closeModal();
      });
    }

    // Keyboard shortcuts
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.closeModal();
    });

    // Form submission
    if (form) {
      form.addEventListener("submit", async (e) => {
        e.preventDefault();
        await this.handleSubmit();
      });
    }
  }

  private async handleSubmit(): Promise<void> {
    // Collect scores
    const sets: any[] = [];

    const set1Team1 = parseInt(
      (this.container.querySelector('input[name="set-1-team1"]') as HTMLInputElement)
        ?.value || "0"
    );
    const set1Team2 = parseInt(
      (this.container.querySelector('input[name="set-1-team2"]') as HTMLInputElement)
        ?.value || "0"
    );

    const set2Team1 = parseInt(
      (this.container.querySelector('input[name="set-2-team1"]') as HTMLInputElement)
        ?.value || "0"
    );
    const set2Team2 = parseInt(
      (this.container.querySelector('input[name="set-2-team2"]') as HTMLInputElement)
        ?.value || "0"
    );

    const set3Team1 = parseInt(
      (this.container.querySelector('input[name="set-3-team1"]') as HTMLInputElement)
        ?.value || "0"
    );
    const set3Team2 = parseInt(
      (this.container.querySelector('input[name="set-3-team2"]') as HTMLInputElement)
        ?.value || "0"
    );

    // Validate Set 1 and Set 2 are required
    if ((set1Team1 === 0 && set1Team2 === 0) || (set2Team1 === 0 && set2Team2 === 0)) {
      showNotification("Please enter scores for at least Sets 1 and 2", "error");
      return;
    }

    // Add Set 1
    sets.push({ pair1score: set1Team1, pair2score: set1Team2 });

    // Add Set 2
    sets.push({ pair1score: set2Team1, pair2score: set2Team2 });

    // Calculate if match is decided
    let team1SetWins = 0;
    let team2SetWins = 0;

    if (set1Team1 > set1Team2) team1SetWins++;
    else if (set1Team2 > set1Team1) team2SetWins++;

    if (set2Team1 > set2Team2) team1SetWins++;
    else if (set2Team2 > set2Team1) team2SetWins++;

    // If sets are split, Set 3 is required
    if (team1SetWins === 1 && team2SetWins === 1) {
      if (set3Team1 === 0 && set3Team2 === 0) {
        showNotification("Sets 1 and 2 are split. Please enter Set 3 score.", "info");
        return;
      }
      sets.push({ pair1score: set3Team1, pair2score: set3Team2 });
    }

    // Final validation check before submission
    const validation = validateMatchScore(sets);
    if (!validation.isValid) {
      showNotification(validation.error || "Invalid match scores", "error");
      return;
    }

    // Disable submit button and show loading state. The inline ring spinner is a
    // transform-only (hardware-accelerated) animation built from Tailwind
    // utilities; `border-current` makes it inherit the button's text colour so
    // it reads correctly in both light and dark skins.
    const submitBtn = this.container.querySelector("#submit-btn") as HTMLButtonElement;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerHTML =
        '<span class="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" aria-hidden="true"></span><span>Submitting…</span>';
    }

    try {
      // Call the onSubmit callback with the sets data
      await this.onSubmit({ sets });
      // Close modal after successful submission
      this.isDirty = false;
      this.closeModal();
    } catch (error) {
      console.error("Failed to submit result:", error);
      showNotification("Failed to submit result. Please try again.", "error");

      // Restore button
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerHTML =
          '<span class="material-symbols-outlined">check_circle</span><span>Submit Match Result</span>';
      }
    }
  }

  private closeModal(): void {
    this.container.dispatchEvent(new CustomEvent("close-report-modal", { bubbles: true }));
  }
}
